import React, { useState } from 'react';
import axios from 'axios';
import { API } from '../utils/api';

/**
 * WelcomeIntroModal - First-time login welcome modal for admin users
 * 
 * Displays on first login to inform admins about:
 * 1. Default organisation settings that have been configured
 * 2. How to access and modify these settings
 * 
 * The modal can be dismissed, and users can opt to not show it again.
 */
const WelcomeIntroModal = ({ isOpen, onClose, organisationName }) => {
  const [dontShowAgain, setDontShowAgain] = useState(false);

  if (!isOpen) return null;

  const handleDismiss = async () => {
    try {
      // Only mark as permanently dismissed if checkbox is checked
      if (dontShowAgain) {
        await axios.post(`${API}/admin/preferences/dismiss-welcome-intro`, {}, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
      }
    } catch (error) {
      console.error('Failed to save welcome intro preference:', error);
    }
    onClose();
  };

  const handleGoToSettings = async () => {
    try {
      // Only mark as permanently dismissed if checkbox is checked
      if (dontShowAgain) {
        await axios.post(`${API}/admin/preferences/dismiss-welcome-intro`, {}, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
      }
    } catch (error) {
      console.error('Failed to save welcome intro preference:', error);
    }
    // Navigate to settings page
    window.location.href = '/admin/team-dashboard-settings';
  };

  // Default organisation settings to display
  const defaultSettings = [
    {
      icon: '🕐',
      setting: 'Work Hours',
      value: '08:00 - 17:00',
      description: 'Default shift times for team members'
    },
    {
      icon: '📅',
      setting: 'Work Days',
      value: 'Monday - Friday',
      description: 'Standard 5-day work week'
    },
    {
      icon: '📸',
      setting: 'Minimum Photos',
      value: '5 per day',
      description: 'Required photos for work diary entries'
    },
    {
      icon: '🤳',
      setting: 'Selfie Required',
      value: 'Disabled',
      description: 'Selfie verification for clock-in'
    },
    {
      icon: '⏰',
      setting: 'Auto Clock-Out',
      value: 'Disabled',
      description: 'Automatic end-of-day clock-out'
    }
  ];

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 p-6 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="text-4xl">🎉</div>
              <div>
                <h2 className="text-2xl font-bold text-white">Welcome to SiteOrbit!</h2>
                <p className="text-blue-100 text-sm mt-1">
                  {organisationName ? `${organisationName} is now set up` : 'Your organisation is now set up'}
                </p>
              </div>
            </div>
            <button
              onClick={handleDismiss}
              className="text-white/70 hover:text-white transition-colors p-2 hover:bg-white/10 rounded-lg"
              aria-label="Close"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Intro Message */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
            <div className="flex items-start space-x-3">
              <div className="text-2xl">ℹ️</div>
              <div>
                <h3 className="font-semibold text-blue-900 mb-1">Default Settings Applied</h3>
                <p className="text-blue-700 text-sm leading-relaxed">
                  We&apos;ve set up your organisation with sensible default settings to help you get started quickly. 
                  You can customise these settings at any time to match your business requirements.
                </p>
              </div>
            </div>
          </div>

          {/* Default Settings List */}
          <h3 className="font-semibold text-gray-800 mb-4 flex items-center">
            <span className="mr-2">⚙️</span>
            Your Current Default Settings
          </h3>
          
          <div className="space-y-3 mb-6">
            {defaultSettings.map((item, index) => (
              <div 
                key={index}
                className="flex items-center p-3 bg-gray-50 rounded-lg border border-gray-100 hover:bg-gray-100 transition-colors"
              >
                <div className="text-2xl mr-4">{item.icon}</div>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-gray-800">{item.setting}</span>
                    <span className="text-sm font-semibold text-indigo-600 bg-indigo-50 px-2 py-1 rounded">
                      {item.value}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">{item.description}</p>
                </div>
              </div>
            ))}
          </div>

          {/* How to Change Settings */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-6">
            <div className="flex items-start space-x-3">
              <div className="text-2xl">💡</div>
              <div>
                <h3 className="font-semibold text-yellow-900 mb-1">Need to Change These?</h3>
                <p className="text-yellow-800 text-sm leading-relaxed">
                  Navigate to <strong>Settings → Organisation Settings</strong> to customise work hours, 
                  required photos, selfie verification, auto clock-out rules, and more.
                </p>
              </div>
            </div>
          </div>

          {/* Quick Tips */}
          <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
            <span className="mr-2">🚀</span>
            Quick Start Tips
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
            <div className="p-3 bg-green-50 rounded-lg border border-green-200">
              <div className="flex items-center space-x-2 mb-1">
                <span className="text-lg">👥</span>
                <span className="font-medium text-green-800 text-sm">Add Team Members</span>
              </div>
              <p className="text-xs text-green-700">Invite your workers to start tracking time</p>
            </div>
            <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
              <div className="flex items-center space-x-2 mb-1">
                <span className="text-lg">🏗️</span>
                <span className="font-medium text-amber-800 text-sm">Create Sites</span>
              </div>
              <p className="text-xs text-amber-700">Set up your construction sites and locations</p>
            </div>
            <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
              <div className="flex items-center space-x-2 mb-1">
                <span className="text-lg">📋</span>
                <span className="font-medium text-purple-800 text-sm">Assign Tasks</span>
              </div>
              <p className="text-xs text-purple-700">Create and assign tasks to team members</p>
            </div>
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center space-x-2 mb-1">
                <span className="text-lg">📊</span>
                <span className="font-medium text-blue-800 text-sm">View Reports</span>
              </div>
              <p className="text-xs text-blue-700">Track progress with detailed reports</p>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="border-t border-gray-100 p-4 bg-gray-50 rounded-b-2xl">
          {/* Don't show again checkbox */}
          <div className="flex items-center mb-4">
            <input
              type="checkbox"
              id="dontShowAgain"
              checked={dontShowAgain}
              onChange={(e) => setDontShowAgain(e.target.checked)}
              className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 cursor-pointer"
            />
            <label htmlFor="dontShowAgain" className="ml-2 text-sm text-gray-600 cursor-pointer select-none">
              Don&apos;t show this again
            </label>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-3 sm:justify-between">
            <button
              onClick={handleGoToSettings}
              className="flex items-center justify-center space-x-2 px-4 py-2.5 text-indigo-600 border border-indigo-200 rounded-lg hover:bg-indigo-50 transition-colors font-medium text-sm"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span>Go to Settings</span>
            </button>
            
            <button
              onClick={handleDismiss}
              className="flex items-center justify-center space-x-2 px-6 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg hover:from-indigo-700 hover:to-purple-700 transition-all font-medium text-sm shadow-md hover:shadow-lg"
            >
              <span>Got it, let&apos;s go!</span>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WelcomeIntroModal;
